import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Divider,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  TipsAndUpdates as TourIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  StopCircle as StopIcon,
  RestartAlt as RestartIcon,
  SupportAgent as SupportIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearAllProgress } from 'src/redux/slices/tour';
import { useTourContext } from 'src/components/TourProvider';
import useAuth from 'src/hooks/useAuth';
import {
  persistTourCompletion,
  persistTourStepReached,
} from 'src/hooks/useTour';
import * as tourAPI from 'src/backend/tour';
import { getTourForPath } from 'src/config/tours';
import { getDiscriminators } from 'src/utils/permissions';

const TourHelpButton = () => {
  const { t } = useTranslation('translation');
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, isImpersonating } = useAuth();
  const {
    startTour,
    isRunning,
    stopTour,
    setToursHidden,
    areToursHidden,
    clearTourSession,
    hideAllToursDecisionTooltipOpen,
    dismissHideAllToursDecisionTooltip,
  } = useTourContext();
  const { progress, settings } = useSelector((state) => state.tour);

  // Executive and corporate accounts have no tours; show support only.
  const orgType = user ? getDiscriminators(user).orgType : null;
  const toursEnabled = orgType !== 'executive' && orgType !== 'corporate';

  const currentTourConfig = isImpersonating
    ? null
    : getTourForPath(location.pathname, user);
  const currentTourKey = currentTourConfig?.tourKey;
  const isCurrentTourCompleted = currentTourKey
    ? progress[currentTourKey]?.completed
    : false;
  const showCurrentTourAttention =
    Boolean(currentTourKey) &&
    Boolean(settings?.isLoaded) &&
    areToursHidden &&
    !isCurrentTourCompleted;
  const startTourLabel = isCurrentTourCompleted
    ? t('tour-help.replay-page-tour')
    : t('tour-help.start-page-tour');
  const visibilityLabel = areToursHidden
    ? t('tour-help.show-incomplete-tours')
    : t('tour-help.hide-all-tours');

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (isImpersonating) return;
    dismissHideAllToursDecisionTooltip();
  }, [location.pathname, dismissHideAllToursDecisionTooltip, isImpersonating]);

  if (isImpersonating) {
    return null;
  }

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStartTour = () => {
    handleClose();
    if (!currentTourConfig?.steps) return;
    const onComplete = (lastStepIndex) => {
      persistTourCompletion(currentTourKey, lastStepIndex, {
        dispatch,
        userId: user?.id,
        teamId: user?.team_id,
        progress,
      });
    };
    const onClose = (currentStepIndex) => {
      persistTourStepReached(currentTourKey, currentStepIndex, {
        dispatch,
        userId: user?.id,
        teamId: user?.team_id,
        progress,
      });
    };
    startTour(
      currentTourConfig.steps,
      currentTourKey,
      onComplete,
      undefined,
      onClose
    );
  };

  const handleToggleTourVisibility = async () => {
    handleClose();
    await setToursHidden(!areToursHidden);
  };

  const handleResetAllTours = async () => {
    handleClose();
    dispatch(clearAllProgress());
    clearTourSession();
    try {
      await tourAPI.resetAllTours();
      // Clear local cache
      if (user?.id && user?.team_id) {
        localStorage.removeItem(`tour_progress_${user.id}_${user.team_id}`);
      }
    } catch {
      // Ignore errors
    }
  };

  const handleStopTour = () => {
    handleClose();
    stopTour();
  };

  return (
    <>
      <Tooltip
        title={
          toursEnabled ? t('tour-help.tours-and-support') : t('sidebar.support')
        }
        arrow
      >
        <Box
          component="span"
          sx={{ display: 'inline-flex', position: 'relative' }}
        >
          <IconButton
            onClick={handleClick}
            onMouseEnter={dismissHideAllToursDecisionTooltip}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <Badge
              color="error"
              variant="dot"
              invisible={!showCurrentTourAttention}
              overlap="circular"
            >
              <HelpIcon />
            </Badge>
          </IconButton>
          <Tooltip
            title={t('tour-help.hide-tours-note')}
            arrow
            open={hideAllToursDecisionTooltipOpen}
            disableHoverListener
            disableFocusListener
            disableTouchListener
            placement="bottom"
          >
            <Box
              component="span"
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
              }}
            />
          </Tooltip>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {toursEnabled && (
          <>
            {currentTourConfig && (
              <MenuItem
                onClick={handleStartTour}
                disabled={isRunning}
                selected={showCurrentTourAttention}
                sx={{
                  ...(showCurrentTourAttention
                    ? {
                        bgcolor: 'action.selected',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        '& .MuiListItemText-primary': {
                          fontWeight: 600,
                        },
                      }
                    : {}),
                }}
              >
                <ListItemIcon>
                  <Badge
                    color="error"
                    variant="dot"
                    invisible={!showCurrentTourAttention}
                    overlap="circular"
                  >
                    <TourIcon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                <ListItemText>{startTourLabel}</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleToggleTourVisibility}>
              <ListItemIcon>
                {areToursHidden ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>{visibilityLabel}</ListItemText>
            </MenuItem>
            {isRunning && (
              <MenuItem onClick={handleStopTour}>
                <ListItemIcon>
                  <StopIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('tour-help.stop-tour')}</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleResetAllTours}>
              <ListItemIcon>
                <RestartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('tour-help.reset-all-tours')}</ListItemText>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
          </>
        )}
        <MenuItem
          component="a"
          href="https://help.careit.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClose}
          sx={{ justifyContent: 'space-between' }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SupportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('sidebar.support')}</ListItemText>
          <ListItemIcon sx={{ minWidth: 36, justifyContent: 'flex-end' }}>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>
    </>
  );
};

export default TourHelpButton;
